// Typed client for the project "presentation" endpoints. A presentation is an
// editable slide deck authored from a project's sources. The canonical form is
// `slides_markdown` (per slide: `# title`, `- bullet` lines, a `> notes` line,
// optional `![figure](query)`; slides separated by a line `---`); the server
// re-derives the outline on every save. Mirrors `api/report.ts`.
import { api } from "./client";

export type PresentationStatus = "pending" | "running" | "complete" | "failed";

export const PRESENTATION_IN_FLIGHT: PresentationStatus[] = ["pending", "running"];

export interface PresentationSlideOutline {
  index: number;
  title: string;
  bullets: string[];
  has_figure: boolean;
  source_ids: string[];
}

export interface PresentationRead {
  id: string;
  project_id: string;
  status: PresentationStatus;
  slides_markdown: string | null;
  outline: PresentationSlideOutline[];
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface PresentationRewriteResult {
  slide_index: number;
  proposed_markdown: string | null;
  rationale: string;
  document: PresentationRead | null;
}

const enc = encodeURIComponent;

export const generatePresentation = (
  id: string,
  opts?: { language?: string; maxSlides?: number; brief?: string },
) =>
  api.post<PresentationRead>(`/projects/${enc(id)}/presentation`, {
    ...(opts?.language ? { language: opts.language } : {}),
    ...(opts?.maxSlides != null ? { max_slides: opts.maxSlides } : {}),
    ...(opts?.brief ? { brief: opts.brief } : {}),
  });

export const getPresentation = (id: string) =>
  api.get<PresentationRead>(`/projects/${enc(id)}/presentation`);

export const savePresentation = (id: string, slidesMarkdown: string) =>
  api.patch<PresentationRead>(`/projects/${enc(id)}/presentation`, {
    slides_markdown: slidesMarkdown,
  });

export const rewritePresentationSlide = (
  id: string,
  body: {
    directive: string;
    slide_index: number;
    language?: string;
    preview: boolean;
  },
) => api.post<PresentationRewriteResult>(`/projects/${enc(id)}/presentation/rewrite`, body);
